/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import React from 'react';
import { PopoverContentWrapper } from './PopoverContentWrapper';
import type { PopoverContentProps } from './types';
import { PopoverCloseButton } from './PopoverCloseButton';
import { usePopoverContext } from './PopoverContext';
import { isReactNative } from '~utils';
import BaseBox from '~components/Box/BaseBox';
import { Text } from '~components/Typography';
import { useIsMobile } from '~utils/useIsMobile';
import { useTheme } from '~components/BladeProvider';

type PopoverHeaderProps = {
  title?: string;
  titleLeading?: React.ReactNode;
};

const PopoverHeader = ({ title, titleLeading }: PopoverHeaderProps): React.ReactElement | null => {
  const { titleId, openInteraction } = usePopoverContext();

  const showCloseButton = openInteraction === 'click';
  const isFloating = !(title || titleLeading);

  if (isFloating) {
    if (!showCloseButton) {
      return null;
    }
    return (
      <BaseBox
        borderRadius="max"
        position="absolute"
        padding="spacing.2"
        top="spacing.3"
        right="spacing.3"
        zIndex={1}
      >
        <PopoverCloseButton />
      </BaseBox>
    );
  }

  return (
    <BaseBox
      display="flex"
      flexDirection="row"
      flexWrap={isReactNative() ? 'wrap' : 'nowrap'}
      alignItems="center"
      gap="spacing.3"
    >
      {titleLeading
        ? React.cloneElement(titleLeading as React.ReactElement, { size: 'medium' })
        : null}
      {title ? (
        <BaseBox id={titleId} paddingRight="spacing.4">
          <Text size="large" weight="semibold">
            {title}
          </Text>
        </BaseBox>
      ) : null}
      {showCloseButton ? (
        <BaseBox marginLeft="auto">
          <PopoverCloseButton />
        </BaseBox>
      ) : null}
    </BaseBox>
  );
};

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ children, title, titleLeading, footer, arrow, side, style, isVisible }, ref) => {
    const isMobile = useIsMobile();
    const { colorScheme } = useTheme();
    return (
      <PopoverContentWrapper
        ref={ref as never}
        styles={style}
        side={side}
        isVisible={isVisible}
        isMobile={isMobile}
        colorScheme={colorScheme}
      >
        <BaseBox padding="spacing.5" display="flex" flexDirection="column" gap="spacing.5">
          <BaseBox display="flex" flexDirection="column" gap="spacing.2">
            <PopoverHeader title={title} titleLeading={titleLeading} />
            <BaseBox>{children}</BaseBox>
          </BaseBox>
          {footer ? <BaseBox>{footer}</BaseBox> : null}
        </BaseBox>
        {arrow}
      </PopoverContentWrapper>
    );
  },
);

export { PopoverContent };
